import * as BoardConstants from "@/game/constants/boardConstants";
import type { BoardDimensions } from "@/game/utils/animationTypes";
import type { Texture } from "pixi.js";
import { useMemo } from "react";

export function useGameBoardLayout(
  parentRect: DOMRect | null,
  boardTexture: Texture | null,
  boardScale: number,
  sampleCheckerTexture: Texture | null,
  isFlipped: boolean
) {
  // Stage size derived from container
  const stageWidth = parentRect?.width ?? 0;
  const stageHeight = parentRect?.height ?? 0;

  // Board scale (fit inside stage)
  boardScale =
    boardTexture && parentRect
      ? Math.min(parentRect.width / boardTexture.width, parentRect.height / boardTexture.height) *
        boardScale
      : 1;

  const boardWidth = boardTexture ? boardTexture.width * boardScale : 0;
  const boardHeight = boardTexture ? boardTexture.height * boardScale : 0;

  // Inner playable rectangle (after padding)
  const playArea = useMemo<[number, number, number, number]>(
    () => [
      stageWidth / 2 - boardWidth / 2 + BoardConstants.BOARD_PADDING[0] * boardScale,
      stageHeight / 2 - boardHeight / 2 + BoardConstants.BOARD_PADDING[1] * boardScale,
      boardWidth - BoardConstants.BOARD_PADDING[2] * boardScale,
      boardHeight - BoardConstants.BOARD_PADDING[3] * boardScale
    ],
    [stageWidth, stageHeight, boardWidth, boardHeight, boardScale]
  );

  // Points
  const getPointArea = (index: number, isTop: boolean): [number, number, number, number] => {
    const pointWidth = (playArea[2] - BoardConstants.BOARD_POINT_WIDTH * boardScale) / 12;
    const pointOffsetX =
      index < 6 ? playArea[0] : playArea[0] + BoardConstants.BOARD_POINT_WIDTH * boardScale;
    const pointOffsetY = isTop ? playArea[1] : playArea[1] + playArea[3] / 2;
    return [pointOffsetX + index * pointWidth, pointOffsetY, pointWidth, playArea[3] / 2];
  };

  // Bars
  const bar1Area: [number, number, number, number] = [
    playArea[0] + playArea[2] / 2 - BoardConstants.BOARD_BAR1_OFFSET[0] * boardScale,
    playArea[1] + playArea[3] / 2 - BoardConstants.BOARD_BAR1_OFFSET[1] * boardScale,
    BoardConstants.BOARD_BAR_AREA_SIZE[0] * boardScale,
    BoardConstants.BOARD_BAR_AREA_SIZE[1] * boardScale
  ];
  const bar2Area: [number, number, number, number] = [
    playArea[0] + playArea[2] / 2 - BoardConstants.BOARD_BAR2_OFFSET[0] * boardScale,
    playArea[1] + playArea[3] / 2 + BoardConstants.BOARD_BAR2_OFFSET[1] * boardScale,
    BoardConstants.BOARD_BAR_AREA_SIZE[0] * boardScale,
    BoardConstants.BOARD_BAR_AREA_SIZE[1] * boardScale
  ];

  // Bear-off (right side)
  const bearOff1Area: [number, number, number, number] = [
    playArea[0] + playArea[2] + BoardConstants.BOARD_BEAR1_OFFSET[0] * boardScale,
    playArea[1] + playArea[3] - BoardConstants.BOARD_BEAR1_OFFSET[1] * boardScale,
    BoardConstants.BOARD_BEAR_AREA_SIZE[0] * boardScale,
    BoardConstants.BOARD_BEAR_AREA_SIZE[1] * boardScale
  ];
  const bearOff2Area: [number, number, number, number] = [
    playArea[0] + playArea[2] + BoardConstants.BOARD_BEAR2_OFFSET[0] * boardScale,
    playArea[1] + BoardConstants.BOARD_BEAR2_OFFSET[1] * boardScale,
    BoardConstants.BOARD_BEAR_AREA_SIZE[0] * boardScale,
    BoardConstants.BOARD_BEAR_AREA_SIZE[1] * boardScale
  ];

  // Dice (right sidebar)
  const diceArea: [number, number, number, number] = [
    playArea[0] + playArea[2] + BoardConstants.BOARD_DICE_AREA[0] * boardScale,
    playArea[1] + BoardConstants.BOARD_DICE_AREA[1] * boardScale,
    BoardConstants.BOARD_DICE_AREA[2] * boardScale,
    BoardConstants.BOARD_DICE_AREA[3] * boardScale
  ];

  // Board dimensions consumed by animation / positioning logic
  const boardDimensions = useMemo<BoardDimensions | null>(() => {
    if (!sampleCheckerTexture || !parentRect) return null;
    const dims: BoardDimensions = {
      pointAreas: {},
      barAreas: {
        white: !isFlipped ? bar1Area : bar2Area,
        black: !isFlipped ? bar2Area : bar1Area
      },
      bearOffAreas: {
        white: !isFlipped ? bearOff1Area : bearOff2Area,
        black: !isFlipped ? bearOff2Area : bearOff1Area
      },
      boardScale,
      unscaledCheckerHeight: sampleCheckerTexture.height,
      boardIsFlipped: isFlipped
    };
    for (let i = 0; i <= 23; i++) {
      // Mirror logic consistent with original component
      dims.pointAreas[i] =
        i <= 11 ? getPointArea(11 - i, isFlipped) : getPointArea(i - 12, !isFlipped);
    }
    return dims;
  }, [
    sampleCheckerTexture,
    parentRect,
    boardScale,
    isFlipped,
    bar1Area,
    bar2Area,
    bearOff1Area,
    bearOff2Area
  ]);

  return {
    stageWidth,
    stageHeight,
    boardScale,
    playArea,
    bar1Area,
    bar2Area,
    bearOff1Area,
    bearOff2Area,
    diceArea,
    boardDimensions,
    getPointArea
  };
}
